import { RootState, store } from '../store/store';
import { examsSelectors } from '../slices/exams/examsSlice';
import { withPosthog } from './posthogClient';

type ExamTypeString =
  | 'CUSTOM'
  | 'NBME'
  | 'COMLEX'
  | 'RESIDENCY'
  | 'FREE_STUDY'
  | 'PRE_CLINICAL'
  | 'NCLEX'
  | undefined;

export const getPrimaryExamContext = () => {
  try {
    const state = store.getState();
    const primaryExam = examsSelectors.selectPrimaryExam(state as RootState);
    if (!primaryExam) {
      return {} as {
        examId?: string;
        examName?: string;
        examType?: ExamTypeString;
      };
    }
    return {
      examId: primaryExam.id,
      examName: primaryExam.name,
      examType: (primaryExam.type as ExamTypeString) ?? undefined,
    };
  } catch {
    return {} as {
      examId?: string;
      examName?: string;
      examType?: ExamTypeString;
    };
  }
};

type CaptureOptions = { withExam?: boolean };
type GameStartedAuthState = 'authenticated' | 'public';
type StandardGameName =
  | 'doctordle'
  | 'question_of_the_day'
  | 'buzzwords'
  | 'ddx_doc'
  | 'survival'
  | 'pokemon_or_drug';

export const capture = (
  event: string,
  props?: Record<string, unknown>,
  options?: CaptureOptions
) => {
  try {
    if (!event || typeof event !== 'string') return;

    const withExam = Boolean(options?.withExam);
    const examContext = withExam ? getPrimaryExamContext() : {};
    const mergedProps = { ...examContext, ...(props || {}) };

    withPosthog((posthog) => {
      posthog.capture(event, mergedProps);
    });
  } catch (err) {
    // Swallow errors to avoid breaking UX; log for debugging
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[analytics.capture] failed', { event, props, err });
    }
  }
};

export const captureGameStarted = (params: {
  game: StandardGameName;
  authState: GameStartedAuthState;
  [key: string]: unknown;
}) => {
  const routePath =
    typeof window !== 'undefined' ? window.location.pathname : undefined;

  const routeSearch =
    typeof window !== 'undefined'
      ? window.location.search || undefined
      : undefined;

  capture('game_started', {
    routePath,
    routeSearch,
    ...params,
  });
};
